/**
 * Process Intelligence — DFG + Variants + Bottlenecks viewer (PRODUCT_SPEC §4.96).
 *
 * Renders the cached `/api/process-intel/{source_type}/{source_id}/latest` payload:
 *   summary.graph  → SVG directly-follows graph (auto-laid-out by topological row)
 *   summary.variants → top-K execution paths (gated by process_intel.variants)
 *   summary.bottlenecks → top-N slowest / error-prone steps
 *
 * Two integrations:
 *   - Workflow editor:  <ProcessIntel sourceType="workflow_run" sourceId={workflowId} onClose=… />
 *                       (right-side overlay, like WorkflowMetrics)
 *   - Case-type admin:  <ProcessIntel sourceType="case" sourceId={caseTypeId} embedded />
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from './config';

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const COLORS = {
  bg: 'var(--bg-primary)',
  panel: 'var(--bg-secondary)',
  border: 'var(--border-color)',
  text: 'var(--text-primary)',
  muted: 'var(--text-secondary)',
  faint: 'var(--text-tertiary)',
  accent: '#3b82f6',
  good: '#22c55e',
  warn: '#f59e0b',
  bad: '#ef4444',
};

function fmtMs(ms) {
  const n = Number(ms || 0);
  if (n < 1000) return `${Math.round(n)} ms`;
  if (n < 60_000) return `${(n / 1000).toFixed(1)} s`;
  return `${(n / 60_000).toFixed(1)} min`;
}

function fmtSec(s) {
  const n = Number(s || 0);
  if (n < 60) return `${n.toFixed(1)} s`;
  if (n < 3600) return `${(n / 60).toFixed(1)} min`;
  return `${(n / 3600).toFixed(1)} h`;
}

function rateColor(r) {
  const v = Number(r || 0);
  if (v >= 0.1) return COLORS.bad;
  if (v >= 0.02) return COLORS.warn;
  return COLORS.good;
}

// Severity colormap for DFG heatmap overlay (P3): green → amber → red.
// `t` in [0, 1]; returns rgba string with alpha = baseAlpha + (1-baseAlpha)*t
// so severity 0 is a faint tint and severity 1 is fully saturated.
function heatColor(t, baseAlpha = 0.18) {
  const clamped = Math.max(0, Math.min(1, Number.isFinite(t) ? t : 0));
  let r;
  let g;
  let b;
  if (clamped < 0.5) {
    const u = clamped * 2;
    r = Math.round(34 + (245 - 34) * u);
    g = Math.round(197 + (158 - 197) * u);
    b = Math.round(94 + (11 - 94) * u);
  } else {
    const u = (clamped - 0.5) * 2;
    r = Math.round(245 + (239 - 245) * u);
    g = Math.round(158 + (68 - 158) * u);
    b = Math.round(11 + (68 - 11) * u);
  }
  const a = baseAlpha + (1 - baseAlpha) * clamped;
  return `rgba(${r}, ${g}, ${b}, ${a.toFixed(3)})`;
}

// ── Topological-ish layout for a DFG ───────────────────────────────────────
// Assigns each node a "row" (depth from sources). Cycles are broken by
// keeping the first edge that improves depth.
function layoutGraph(graph) {
  const nodes = (graph?.nodes || []).map((n) => ({ ...n }));
  const edges = graph?.edges || [];
  if (!nodes.length) return { nodes: [], edges: [], width: 0, height: 0 };

  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const inDeg = new Map(nodes.map((n) => [n.id, 0]));
  const outAdj = new Map(nodes.map((n) => [n.id, []]));
  edges.forEach((e) => {
    if (!nodeById.has(e.from) || !nodeById.has(e.to)) return;
    inDeg.set(e.to, (inDeg.get(e.to) || 0) + 1);
    outAdj.get(e.from).push(e.to);
  });

  // BFS layered ordering (Kahn). Cycles get whatever depth they end up at.
  const depth = new Map(nodes.map((n) => [n.id, 0]));
  const queue = [];
  inDeg.forEach((d, id) => { if (d === 0) queue.push(id); });
  if (!queue.length) queue.push(nodes[0].id); // fully-cyclic fallback
  const visited = new Set();
  while (queue.length) {
    const id = queue.shift();
    if (visited.has(id)) continue;
    visited.add(id);
    const d = depth.get(id) || 0;
    (outAdj.get(id) || []).forEach((to) => {
      const nd = Math.max(depth.get(to) || 0, d + 1);
      depth.set(to, nd);
      if (!visited.has(to)) queue.push(to);
    });
  }
  // Any unvisited (in a SCC) get assigned by their first incoming edge's depth.
  nodes.forEach((n) => {
    if (!visited.has(n.id)) depth.set(n.id, 0);
  });

  // Group by row.
  const rows = new Map();
  nodes.forEach((n) => {
    const r = depth.get(n.id) || 0;
    if (!rows.has(r)) rows.set(r, []);
    rows.get(r).push(n);
  });
  const sortedRows = [...rows.keys()].sort((a, b) => a - b);
  // Sort within row by event_count desc for visual stability.
  sortedRows.forEach((r) => rows.get(r).sort((a, b) => (b.event_count || 0) - (a.event_count || 0)));

  const NODE_W = 160;
  const NODE_H = 56;
  const ROW_GAP = 90;
  const COL_GAP = 32;

  const maxCols = Math.max(1, ...sortedRows.map((r) => rows.get(r).length));
  const width = Math.max(640, maxCols * (NODE_W + COL_GAP) + COL_GAP);
  const height = sortedRows.length * (NODE_H + ROW_GAP) + ROW_GAP;

  const placed = [];
  sortedRows.forEach((r, rIdx) => {
    const list = rows.get(r);
    const rowWidth = list.length * NODE_W + (list.length - 1) * COL_GAP;
    const startX = (width - rowWidth) / 2;
    const y = ROW_GAP / 2 + rIdx * (NODE_H + ROW_GAP);
    list.forEach((n, i) => {
      placed.push({
        ...n,
        x: startX + i * (NODE_W + COL_GAP),
        y,
        w: NODE_W,
        h: NODE_H,
      });
    });
  });

  const placedById = new Map(placed.map((n) => [n.id, n]));
  const placedEdges = edges
    .map((e) => {
      const a = placedById.get(e.from);
      const b = placedById.get(e.to);
      if (!a || !b) return null;
      return { ...e, a, b };
    })
    .filter(Boolean);
  return { nodes: placed, edges: placedEdges, width, height };
}

function maxEdgeCount(edges) {
  return edges.reduce((m, e) => Math.max(m, e.count || 0), 1);
}

function DfgCanvas({ graph, onSelectNode, selectedNodeId, heatmap }) {
  const layout = useMemo(() => layoutGraph(graph), [graph]);
  const maxCount = useMemo(() => maxEdgeCount(layout.edges), [layout.edges]);

  if (!layout.nodes.length) {
    return (
      <div style={{
        padding: 40, textAlign: 'center', color: COLORS.muted, fontSize: 13,
      }}>
        No process events yet. Run this {graph?._sourceType === 'case' ? 'case type' : 'workflow'} a few times then refresh.
      </div>
    );
  }

  return (
    <svg
      width="100%"
      viewBox={`0 0 ${layout.width} ${layout.height}`}
      style={{ background: COLORS.bg, display: 'block' }}
    >
      <defs>
        <marker id="pi-arrow" viewBox="0 0 10 10" refX="9" refY="5"
                markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={COLORS.muted} />
        </marker>
      </defs>

      {/* Edges first so nodes paint over them */}
      {layout.edges.map((e, i) => {
        const x1 = e.a.x + e.a.w / 2;
        const y1 = e.a.y + e.a.h;
        const x2 = e.b.x + e.b.w / 2;
        const y2 = e.b.y;
        const mx = (x1 + x2) / 2;
        const my = (y1 + y2) / 2;
        const isLoop = e.from === e.to;
        const supportPct = (e.count || 0) / maxCount;
        const strokeW = 1 + supportPct * 3;
        const opacity = 0.35 + supportPct * 0.5;
        const path = isLoop
          ? `M ${e.a.x + e.a.w - 8} ${e.a.y + 8}
             c 30 -22 70 -22 30 16`
          : `M ${x1} ${y1} C ${x1} ${(y1 + y2) / 2}, ${x2} ${(y1 + y2) / 2}, ${x2} ${y2}`;
        return (
          <g key={`edge-${i}`}>
            <path
              d={path}
              stroke={COLORS.muted}
              strokeWidth={strokeW}
              fill="none"
              opacity={opacity}
              markerEnd="url(#pi-arrow)"
            />
            {!isLoop && (
              <text x={mx} y={my} fontSize="10" fill={COLORS.muted} textAnchor="middle"
                    style={{ paintOrder: 'stroke', stroke: COLORS.bg, strokeWidth: 3 }}>
                {e.count}× · {fmtMs(e.mean_wait_ms)}
              </text>
            )}
          </g>
        );
      })}

      {/* Nodes */}
      {layout.nodes.map((n) => {
        const sel = selectedNodeId === n.id;
        const sev = heatmap?.sev?.get(n.id);
        const tinted = heatmap && sev != null;
        const baseFill = tinted ? heatColor(sev) : COLORS.panel;
        const bg = sel ? COLORS.accent : baseFill;
        const fg = sel ? '#fff' : COLORS.text;
        const stroke = sel
          ? COLORS.accent
          : (tinted ? heatColor(sev, 0.55) : COLORS.border);
        const valLabel = heatmap?.valMap?.get(n.id);
        return (
          <g key={n.id} transform={`translate(${n.x},${n.y})`}
             style={{ cursor: 'pointer' }}
             onClick={() => onSelectNode && onSelectNode(n.id)}>
            <title>{n.id}{valLabel ? ` — ${heatmap.label}: ${valLabel}` : ''}</title>
            <rect width={n.w} height={n.h} rx="8" ry="8"
                  fill={bg} stroke={stroke} strokeWidth={tinted ? 2 : 1.5} />
            <text x={n.w / 2} y={20} fontSize="12" fontWeight="600" fill={fg} textAnchor="middle">
              {n.id.length > 22 ? `${n.id.slice(0, 21)}…` : n.id}
            </text>
            <text x={n.w / 2} y={36} fontSize="10" fill={sel ? '#cfe2ff' : COLORS.muted} textAnchor="middle">
              {n.event_count}× · {fmtMs(n.mean_active_ms)}
            </text>
            <circle cx={12} cy={n.h - 12} r="4" fill={rateColor(n.error_rate)} />
            <text x={20} y={n.h - 8} fontSize="9" fill={sel ? '#cfe2ff' : COLORS.faint}>
              err {Math.round((n.error_rate || 0) * 100)}%
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function VariantsList({ variants, locked, lockedMessage }) {
  if (locked) {
    return (
      <div style={{ padding: 16, fontSize: 12, color: COLORS.muted }}>
        🔒 {lockedMessage || 'Variant clustering requires Pro or higher.'}
      </div>
    );
  }
  if (!variants || variants.length === 0) {
    return <div style={{ padding: 16, fontSize: 12, color: COLORS.muted }}>No variants yet.</div>;
  }
  const total = variants.reduce((s, v) => s + (v.count || 0), 0) || 1;
  return (
    <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
      {variants.map((v) => {
        const pct = ((v.count || 0) / total) * 100;
        return (
          <div key={v.path_hash} style={{
            background: COLORS.panel,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 6,
            padding: '8px 10px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
              <div style={{ fontSize: 11, color: COLORS.faint }}>
                {v.count}× · {pct.toFixed(1)}% · avg {fmtSec(v.avg_duration_s)} · p95 {fmtSec(v.p95_duration_s)}
              </div>
            </div>
            <div style={{
              marginTop: 4, fontSize: 12, fontFamily: 'monospace',
              color: COLORS.text, overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {(v.path || []).join(' → ')}
            </div>
            <div style={{
              marginTop: 6, height: 3, background: COLORS.border, borderRadius: 2, overflow: 'hidden',
            }}>
              <div style={{ height: '100%', width: `${pct}%`, background: COLORS.accent }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ConformancePanel({ conformance, locked, lockedMessage, onSelect, selectedNodeId }) {
  if (locked) {
    return (
      <div style={{ padding: 16, fontSize: 12, color: COLORS.muted }}>
        🔒 {lockedMessage || 'Conformance analysis requires Pro or higher.'}
      </div>
    );
  }
  if (!conformance) {
    return <div style={{ padding: 16, fontSize: 12, color: COLORS.muted }}>Loading conformance…</div>;
  }
  if (conformance.available === false) {
    return (
      <div style={{ padding: 16, fontSize: 12, color: COLORS.muted }}>
        Designed graph unavailable for this source. Conformance needs a workflow definition
        or stage list to compare observed traces against.
      </div>
    );
  }
  if (!conformance.run_count) {
    return (
      <div style={{ padding: 16, fontSize: 12, color: COLORS.muted }}>
        No observed runs yet — run the workflow / progress some cases, then re-analyze.
      </div>
    );
  }
  const fitness = conformance.fitness_score;
  const fitColor = fitness == null
    ? COLORS.muted
    : fitness >= 0.9 ? COLORS.good
    : fitness >= 0.7 ? COLORS.warn
    : COLORS.bad;
  const compliantPct = conformance.run_count
    ? Math.round((conformance.compliant_run_count / conformance.run_count) * 100)
    : 0;
  const devCounts = conformance.deviation_counts || { extra: 0, unordered: 0, skipped: 0 };
  const perNode = (conformance.per_node_deviations || []).filter((n) => n.deviation_rate > 0);
  const recent = conformance.recent_deviations || [];

  const devLabel = (k) => k === 'extra' ? 'Extra' : k === 'unordered' ? 'Unordered' : 'Skipped';
  const devColor = (k) => k === 'extra' ? COLORS.bad : k === 'unordered' ? COLORS.warn : COLORS.muted;

  return (
    <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Headline: fitness score + compliant ratio */}
      <div style={{
        background: COLORS.panel,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 6,
        padding: '10px 12px',
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: fitColor }}>
            {fitness == null ? '—' : `${Math.round(fitness * 100)}%`}
          </div>
          <div style={{ fontSize: 11, color: COLORS.muted }}>fitness score</div>
        </div>
        <div style={{ fontSize: 11, color: COLORS.faint, marginTop: 4 }}>
          {conformance.compliant_run_count} / {conformance.run_count} runs compliant ({compliantPct}%) ·
          {' '}{conformance.designed_node_count} designed nodes / {conformance.designed_edge_count} edges
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
          {['extra', 'unordered', 'skipped'].map((k) => (
            <div key={k} style={{
              flex: 1,
              background: COLORS.bg,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 4,
              padding: '4px 6px',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: devColor(k) }}>
                {devCounts[k] || 0}
              </div>
              <div style={{ fontSize: 9, color: COLORS.faint, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {devLabel(k)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Per-node deviation rate */}
      {perNode.length > 0 && (
        <div>
          <div style={{ fontSize: 10, color: COLORS.faint, textTransform: 'uppercase', letterSpacing: 0.6, padding: '4px 4px 6px' }}>
            Per-node deviation
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {perNode.slice(0, 12).map((n) => {
              const sel = selectedNodeId === n.step_key;
              const pct = Math.min(100, Math.round(n.deviation_rate * 100));
              return (
                <div key={n.step_key}
                     onClick={() => onSelect && onSelect(n.step_key)}
                     style={{
                       background: sel ? COLORS.accent : COLORS.panel,
                       color: sel ? '#fff' : COLORS.text,
                       border: `1px solid ${sel ? COLORS.accent : COLORS.border}`,
                       borderRadius: 4,
                       padding: '6px 8px',
                       cursor: 'pointer',
                     }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, gap: 6 }}>
                    <div style={{ fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {n.in_designed_graph ? '' : '⚠ '}{n.step_key}
                    </div>
                    <div style={{
                      fontWeight: 700,
                      color: sel ? '#fff' : (pct >= 30 ? COLORS.bad : pct >= 10 ? COLORS.warn : COLORS.muted),
                    }}>{pct}%</div>
                  </div>
                  <div style={{ fontSize: 9, color: sel ? '#cfe2ff' : COLORS.faint, marginTop: 2 }}>
                    {n.extra_count ? `extra ${n.extra_count} · ` : ''}
                    {n.unordered_count ? `unordered ${n.unordered_count} · ` : ''}
                    {n.skipped_count ? `skipped ${n.skipped_count}` : ''}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent deviations */}
      {recent.length > 0 && (
        <div>
          <div style={{ fontSize: 10, color: COLORS.faint, textTransform: 'uppercase', letterSpacing: 0.6, padding: '4px 4px 6px' }}>
            Recent deviations
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {recent.slice(0, 15).map((d, i) => (
              <div key={i} style={{
                background: COLORS.panel,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 4,
                padding: '4px 8px',
                fontSize: 11,
              }}>
                <span style={{ color: devColor(d.kind), fontWeight: 600 }}>{devLabel(d.kind)}</span>
                {' '}
                <span style={{ fontFamily: 'monospace' }}>{d.step_key}</span>
                {d.prev && <span style={{ color: COLORS.faint }}> (after {d.prev})</span>}
                <div style={{ fontSize: 9, color: COLORS.faint }}>
                  {d.source_id ? `${d.source_id.slice(0, 12)}…` : ''}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BottlenecksList({ bottlenecks, onSelect, selectedNodeId }) {
  if (!bottlenecks || bottlenecks.length === 0) {
    return <div style={{ padding: 16, fontSize: 12, color: COLORS.muted }}>No bottleneck signal yet.</div>;
  }
  return (
    <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
      {bottlenecks.map((b) => {
        const sel = selectedNodeId === b.step_key;
        return (
          <div key={b.step_key}
               onClick={() => onSelect && onSelect(b.step_key)}
               style={{
                 background: sel ? COLORS.accent : COLORS.panel,
                 color: sel ? '#fff' : COLORS.text,
                 border: `1px solid ${sel ? COLORS.accent : COLORS.border}`,
                 borderRadius: 6,
                 padding: '8px 10px',
                 cursor: 'pointer',
               }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{b.step_key}</div>
              <div style={{
                fontSize: 11, fontWeight: 700,
                color: sel ? '#fff' : (b.score >= 1 ? COLORS.bad : COLORS.warn),
              }}>
                z={b.score.toFixed(2)}
              </div>
            </div>
            <div style={{ fontSize: 11, color: sel ? '#cfe2ff' : COLORS.faint, marginTop: 2 }}>
              mean {fmtMs(b.mean_active_ms)} · err {Math.round((b.error_rate || 0) * 100)}% · SLA {Math.round((b.sla_contribution || 0) * 100)}%
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Simulation panel (§12.64 P5) ───────────────────────────────────────────
// Lets the operator edit per-step service-time + routing overrides on top of
// the latest cached analysis and run a Monte Carlo what-if. Renders the
// resulting cycle-time forecast + bottleneck-rank shift side-by-side with
// baseline so the impact is obvious.

function SimulationPanel({ sourceType, sourceId, analysis, onClose }) {
  const baselineNodes = analysis?.summary?.graph?.nodes || [];
  const baselineEdges = analysis?.summary?.graph?.edges || [];
  const [traceCount, setTraceCount] = useState(1000);
  const [serviceOverrides, setServiceOverrides] = useState({});
  const [routingOverrides, setRoutingOverrides] = useState({});
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const setServiceFor = (stepKey, key, val) => {
    setServiceOverrides((prev) => ({
      ...prev,
      [stepKey]: { ...(prev[stepKey] || {}), [key]: val },
    }));
  };

  const outgoingFromNode = useMemo(() => {
    const m = {};
    baselineEdges.forEach((e) => {
      if (!m[e.from]) m[e.from] = [];
      m[e.from].push({ to: e.to, count: e.count || 0 });
    });
    return m;
  }, [baselineEdges]);

  const run = useCallback(async () => {
    setRunning(true);
    setError(null);
    try {
      const body = {
        trace_count: Math.max(1, Math.min(20000, parseInt(traceCount, 10) || 1000)),
        service_time_overrides: serviceOverrides,
        routing_overrides: routingOverrides,
      };
      const r = await fetch(
        `${API_BASE_URL}/api/process-intel/${sourceType}/${sourceId}/simulate`,
        {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      );
      if (r.status === 402) {
        const b = await r.json().catch(() => null);
        setError(`🔒 ${b?.detail?.message || 'Simulation requires an Enterprise plan.'}`);
        return;
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
      const body2 = await r.json();
      if (body2.status === 'error') {
        setError(body2.error_text || 'Simulation failed');
        return;
      }
      setResult(body2.result || null);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setRunning(false);
    }
  }, [sourceType, sourceId, traceCount, serviceOverrides, routingOverrides]);

  return (
    <div style={{
      position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)',
      zIndex: 10, display: 'flex', alignItems: 'stretch', justifyContent: 'flex-end',
    }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: 'min(720px, 100%)', background: COLORS.bg, color: COLORS.text,
        borderLeft: `1px solid ${COLORS.border}`,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px',
          borderBottom: `1px solid ${COLORS.border}`, background: COLORS.panel, flexShrink: 0,
        }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>🧪 Simulation Sandbox</div>
          <div style={{ flex: 1 }} />
          <button onClick={run} disabled={running} style={{
            background: COLORS.accent, color: '#fff', border: 'none',
            borderRadius: 6, padding: '6px 14px', cursor: running ? 'wait' : 'pointer',
            fontSize: 12, fontWeight: 600, opacity: running ? 0.6 : 1,
          }}>{running ? 'Running…' : 'Run simulation'}</button>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: COLORS.muted,
            fontSize: 18, cursor: 'pointer', padding: '2px 6px',
          }}>✕</button>
        </div>

        {error && (
          <div style={{ padding: '8px 18px', background: '#3a1d1d', color: '#ffb4b4', fontSize: 12 }}>
            {error}
          </div>
        )}

        <div style={{ flex: 1, overflow: 'auto', padding: '12px 18px', fontSize: 12 }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ color: COLORS.muted, marginRight: 8 }}>Trace count:</label>
            <input
              type="number" min={1} max={20000} value={traceCount}
              onChange={(e) => setTraceCount(e.target.value)}
              style={{
                background: COLORS.panel, color: COLORS.text, border: `1px solid ${COLORS.border}`,
                borderRadius: 4, padding: '4px 8px', width: 100,
              }}
            />
          </div>

          <div style={{ fontWeight: 700, color: COLORS.muted, marginBottom: 6 }}>
            Service-time overrides (ms)
          </div>
          <div style={{ marginBottom: 16 }}>
            {baselineNodes.length === 0 && (
              <div style={{ color: COLORS.faint }}>No nodes — run analysis first.</div>
            )}
            {baselineNodes.map((n) => {
              const ov = serviceOverrides[n.id] || {};
              return (
                <div key={n.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4,
                  padding: '4px 8px', background: COLORS.panel, borderRadius: 4,
                }}>
                  <div style={{ flex: 1, fontFamily: 'monospace', fontSize: 11 }}>{n.id}</div>
                  <span style={{ color: COLORS.faint, fontSize: 10 }}>
                    base {fmtMs(n.mean_active_ms)}
                  </span>
                  <input type="number" placeholder={String(Math.round(n.mean_active_ms || 0))}
                    value={ov.mean_ms ?? ''}
                    onChange={(e) => setServiceFor(n.id, 'mean_ms', e.target.value === '' ? undefined : Number(e.target.value))}
                    style={{
                      background: COLORS.bg, color: COLORS.text, border: `1px solid ${COLORS.border}`,
                      borderRadius: 4, padding: '2px 6px', width: 80, fontSize: 11,
                    }}
                  />
                  <select value={ov.distribution || 'lognormal'}
                    onChange={(e) => setServiceFor(n.id, 'distribution', e.target.value)}
                    style={{
                      background: COLORS.bg, color: COLORS.text, border: `1px solid ${COLORS.border}`,
                      borderRadius: 4, padding: '2px 6px', fontSize: 11,
                    }}>
                    <option value="lognormal">lognormal</option>
                    <option value="normal">normal</option>
                    <option value="exponential">exponential</option>
                    <option value="constant">constant</option>
                  </select>
                </div>
              );
            })}
          </div>

          {result && (
            <div style={{ marginTop: 18 }}>
              <div style={{ fontWeight: 700, color: COLORS.muted, marginBottom: 6 }}>
                Result · {result.trace_count} traces
              </div>
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12,
              }}>
                <div style={{
                  background: COLORS.panel, padding: '8px 10px', borderRadius: 6,
                }}>
                  <div style={{ color: COLORS.faint, fontSize: 10, marginBottom: 2 }}>Mean cycle</div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{fmtMs(result.metrics?.mean_cycle_time_ms)}</div>
                  <div style={{ color: COLORS.faint, fontSize: 10 }}>
                    base {fmtMs(result.metrics?.baseline_mean_cycle_time_ms)}
                    {result.metrics?.delta_pct != null && (
                      <span style={{
                        marginLeft: 6, fontWeight: 700,
                        color: result.metrics.delta_pct < 0 ? COLORS.good : COLORS.bad,
                      }}>{result.metrics.delta_pct > 0 ? '+' : ''}{result.metrics.delta_pct}%</span>
                    )}
                  </div>
                </div>
                <div style={{ background: COLORS.panel, padding: '8px 10px', borderRadius: 6 }}>
                  <div style={{ color: COLORS.faint, fontSize: 10, marginBottom: 2 }}>p95 cycle</div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{fmtMs(result.metrics?.p95_cycle_time_ms)}</div>
                </div>
                <div style={{ background: COLORS.panel, padding: '8px 10px', borderRadius: 6 }}>
                  <div style={{ color: COLORS.faint, fontSize: 10, marginBottom: 2 }}>Completion</div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>
                    {Math.round((result.metrics?.completion_rate || 0) * 100)}%
                  </div>
                </div>
              </div>

              <div style={{ fontWeight: 700, color: COLORS.muted, marginBottom: 6 }}>
                Per-step Δ
              </div>
              <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ color: COLORS.faint, textAlign: 'left' }}>
                    <th style={{ padding: 4 }}>Step</th>
                    <th style={{ padding: 4 }}>Mean (ms)</th>
                    <th style={{ padding: 4 }}>Baseline</th>
                    <th style={{ padding: 4 }}>Δ</th>
                    <th style={{ padding: 4 }}>Visit rate</th>
                  </tr>
                </thead>
                <tbody>
                  {(result.per_step || []).map((r) => (
                    <tr key={r.step_key} style={{ borderTop: `1px solid ${COLORS.border}` }}>
                      <td style={{ padding: 4, fontFamily: 'monospace' }}>{r.step_key}</td>
                      <td style={{ padding: 4 }}>{Math.round(r.mean_active_ms || 0)}</td>
                      <td style={{ padding: 4, color: COLORS.faint }}>{Math.round(r.baseline_mean_active_ms || 0)}</td>
                      <td style={{
                        padding: 4, fontWeight: 700,
                        color: r.delta_pct == null
                          ? COLORS.faint
                          : r.delta_pct < 0 ? COLORS.good : COLORS.bad,
                      }}>
                        {r.delta_pct == null ? '—' : `${r.delta_pct > 0 ? '+' : ''}${r.delta_pct}%`}
                      </td>
                      <td style={{ padding: 4 }}>{Math.round((r.visit_rate || 0) * 100)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {result.bottleneck_shift?.length > 0 && (
                <>
                  <div style={{ fontWeight: 700, color: COLORS.muted, marginTop: 12, marginBottom: 6 }}>
                    Bottleneck rank shift
                  </div>
                  <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ color: COLORS.faint, textAlign: 'left' }}>
                        <th style={{ padding: 4 }}>Step</th>
                        <th style={{ padding: 4 }}>Baseline rank</th>
                        <th style={{ padding: 4 }}>Sim rank</th>
                        <th style={{ padding: 4 }}>Δ rank</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.bottleneck_shift.map((b) => (
                        <tr key={b.step_key} style={{ borderTop: `1px solid ${COLORS.border}` }}>
                          <td style={{ padding: 4, fontFamily: 'monospace' }}>{b.step_key}</td>
                          <td style={{ padding: 4, color: COLORS.faint }}>{b.baseline_rank ?? '—'}</td>
                          <td style={{ padding: 4 }}>{b.simulated_rank ?? '—'}</td>
                          <td style={{
                            padding: 4, fontWeight: 700,
                            color: b.delta_rank == null
                              ? COLORS.faint
                              : b.delta_rank > 0 ? COLORS.good : (b.delta_rank < 0 ? COLORS.bad : COLORS.muted),
                          }}>
                            {b.delta_rank == null ? '—' : (b.delta_rank > 0 ? `+${b.delta_rank}` : String(b.delta_rank))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


export default function ProcessIntel({
  sourceType,
  sourceId,
  sourceName,
  embedded = false,
  onClose,
}) {
  const [analysis, setAnalysis] = useState(null);
  const [variants, setVariants] = useState([]);
  const [variantsLocked, setVariantsLocked] = useState(false);
  const [variantsLockedMsg, setVariantsLockedMsg] = useState('');
  const [conformance, setConformance] = useState(null);
  const [conformanceLocked, setConformanceLocked] = useState(false);
  const [conformanceLockedMsg, setConformanceLockedMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [simOpen, setSimOpen] = useState(false);
  const [heatmapMode, setHeatmapMode] = useState('conformance');

  const loadLatest = useCallback(async () => {
    if (!sourceType || !sourceId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(
        `${API_BASE_URL}/api/process-intel/${sourceType}/${sourceId}/latest?refresh_if_stale=false`,
        { headers: authHeaders() },
      );
      if (r.status === 402) {
        const body = await r.json().catch(() => null);
        setError(`🔒 ${body?.detail?.message || 'Process Intelligence requires a higher plan.'}`);
        setAnalysis(null);
        return;
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const text = await r.text();
      const body = text ? JSON.parse(text) : null;
      setAnalysis(body);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, [sourceType, sourceId]);

  const loadVariants = useCallback(async () => {
    if (!sourceType || !sourceId) return;
    try {
      const r = await fetch(
        `${API_BASE_URL}/api/process-intel/${sourceType}/${sourceId}/variants`,
        { headers: authHeaders() },
      );
      if (r.status === 402) {
        const body = await r.json().catch(() => null);
        setVariantsLocked(true);
        setVariantsLockedMsg(body?.detail?.message || '');
        setVariants([]);
        return;
      }
      setVariantsLocked(false);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setVariants(await r.json());
    } catch (e) {
      setVariants([]);
    }
  }, [sourceType, sourceId]);

  const loadConformance = useCallback(async () => {
    if (!sourceType || !sourceId) return;
    try {
      const r = await fetch(
        `${API_BASE_URL}/api/process-intel/${sourceType}/${sourceId}/conformance?refresh_if_stale=false`,
        { headers: authHeaders() },
      );
      if (r.status === 402) {
        const body = await r.json().catch(() => null);
        setConformanceLocked(true);
        setConformanceLockedMsg(body?.detail?.message || '');
        setConformance(null);
        return;
      }
      setConformanceLocked(false);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const body = await r.json();
      setConformance(body?.conformance || null);
    } catch (e) {
      setConformance(null);
    }
  }, [sourceType, sourceId]);

  const runAnalyze = useCallback(async () => {
    if (!sourceType || !sourceId) return;
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(
        `${API_BASE_URL}/api/process-intel/${sourceType}/${sourceId}/analyze`,
        {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        },
      );
      if (r.status === 402) {
        const body = await r.json().catch(() => null);
        setError(`🔒 ${body?.detail?.message || 'Process Intelligence requires a higher plan.'}`);
        return;
      }
      if (r.status === 403) {
        setError('You need editor access on this workspace to run an analysis.');
        return;
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
      const body = await r.json();
      setAnalysis(body);
      loadVariants();
      loadConformance();
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }, [sourceType, sourceId, loadVariants, loadConformance]);

  useEffect(() => {
    setAnalysis(null);
    setVariants([]);
    setConformance(null);
    setSelectedNodeId(null);
    loadLatest();
    loadVariants();
    loadConformance();
  }, [loadLatest, loadVariants, loadConformance]);

  const summary = analysis?.summary || null;
  const graph = summary?.graph || { nodes: [], edges: [] };
  graph._sourceType = sourceType;
  const bottlenecks = summary?.bottlenecks || [];

  // Conformance heatmap is only meaningful when conformance evaluation is
  // available (designed graph present + runs observed). Otherwise we still
  // let the user pick errors/volume/latency since those derive from the DFG.
  const conformanceUnavailable = conformanceLocked
    || (conformance && conformance.available === false)
    || (conformance && !conformance.per_node_deviations);

  const heatmapData = useMemo(() => {
    const nodes = summary?.graph?.nodes || [];
    if (heatmapMode === 'off' || !nodes.length) return null;
    const sev = new Map();
    const valMap = new Map();
    let label = '';
    let legend = '';
    if (heatmapMode === 'conformance') {
      if (conformanceUnavailable) return null;
      label = 'Deviation rate';
      legend = '0% → 30%+';
      const rates = new Map(
        (conformance?.per_node_deviations || []).map((n) => [n.step_key, n.deviation_rate]),
      );
      nodes.forEach((n) => {
        const r = rates.get(n.id) || 0;
        sev.set(n.id, Math.min(1, r / 0.3));
        valMap.set(n.id, `${(r * 100).toFixed(1)}%`);
      });
    } else if (heatmapMode === 'errors') {
      label = 'Error rate';
      legend = '0% → 10%+';
      nodes.forEach((n) => {
        const r = n.error_rate || 0;
        sev.set(n.id, Math.min(1, r / 0.1));
        valMap.set(n.id, `${(r * 100).toFixed(1)}%`);
      });
    } else if (heatmapMode === 'volume') {
      label = 'Event volume';
      const maxV = Math.max(1, ...nodes.map((n) => n.event_count || 0));
      legend = `1 → ${maxV}`;
      nodes.forEach((n) => {
        sev.set(n.id, (n.event_count || 0) / maxV);
        valMap.set(n.id, `${n.event_count || 0} events`);
      });
    } else if (heatmapMode === 'latency') {
      label = 'Mean active time';
      const maxL = Math.max(1, ...nodes.map((n) => n.mean_active_ms || 0));
      legend = `0 → ${fmtMs(maxL)}`;
      nodes.forEach((n) => {
        sev.set(n.id, (n.mean_active_ms || 0) / maxL);
        valMap.set(n.id, fmtMs(n.mean_active_ms || 0));
      });
    } else {
      return null;
    }
    return { sev, valMap, label, legend, mode: heatmapMode };
  }, [heatmapMode, summary, conformance, conformanceUnavailable]);

  const container = embedded
    ? {
        background: COLORS.bg, color: COLORS.text, height: '100%',
        display: 'flex', flexDirection: 'column',
      }
    : {
        position: 'fixed', top: 0, right: 0, width: 'min(1100px, 95vw)', height: '100vh',
        background: COLORS.bg, color: COLORS.text, zIndex: 1000,
        borderLeft: `1px solid ${COLORS.border}`,
        display: 'flex', flexDirection: 'column',
      };

  const finishedAt = analysis?.finished_at ? new Date(analysis.finished_at) : null;

  return (
    <div style={container}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '12px 18px', borderBottom: `1px solid ${COLORS.border}`,
        background: COLORS.panel, flexShrink: 0,
      }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>
            🧭 Process Intelligence
          </div>
          <div style={{ fontSize: 11, color: COLORS.faint, marginTop: 2 }}>
            {sourceName || sourceId} · {sourceType === 'case' ? 'case type' : 'workflow'}
            {finishedAt && (
              <> · analyzed {finishedAt.toLocaleString()}</>
            )}
            {analysis && (
              <> · {analysis.event_count} events · {analysis.variant_count} variants</>
            )}
          </div>
        </div>
        <div style={{ flex: 1 }} />
        {analysis && (
          <button onClick={() => setSimOpen(true)} style={{
            background: COLORS.panel, color: COLORS.text,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 6, padding: '6px 12px', cursor: 'pointer',
            fontSize: 12, fontWeight: 600,
          }} title="Run a what-if Monte Carlo simulation">
            🧪 Simulate
          </button>
        )}
        <button onClick={runAnalyze} disabled={busy} style={{
          background: COLORS.accent, color: '#fff', border: 'none',
          borderRadius: 6, padding: '6px 14px', cursor: busy ? 'wait' : 'pointer',
          fontSize: 12, fontWeight: 600, opacity: busy ? 0.6 : 1,
        }}>
          {busy ? 'Analyzing…' : (analysis ? 'Re-analyze' : 'Run analysis')}
        </button>
        {!embedded && onClose && (
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: COLORS.muted,
            fontSize: 18, cursor: 'pointer', padding: '2px 6px',
          }}>✕</button>
        )}
      </div>

      {error && (
        <div style={{
          padding: '8px 18px', background: '#3a1d1d',
          color: '#ffb4b4', fontSize: 12, borderBottom: `1px solid ${COLORS.border}`,
        }}>
          {error}
        </div>
      )}

      {loading && !analysis && (
        <div style={{ padding: 24, color: COLORS.muted, fontSize: 13 }}>Loading…</div>
      )}

      {!loading && !analysis && !error && (
        <div style={{ padding: 24, color: COLORS.muted, fontSize: 13 }}>
          No analysis cached yet. Click <b>Run analysis</b> to mine the directly-follows graph,
          variants, and bottlenecks from the runs of this {sourceType === 'case' ? 'case type' : 'workflow'}.
        </div>
      )}

      {analysis && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Heatmap selector (P3) */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
            padding: '6px 12px',
            background: COLORS.bg,
            borderBottom: `1px solid ${COLORS.border}`,
            fontSize: 11,
          }}>
            <span style={{ color: COLORS.muted, fontWeight: 600 }}>Heatmap:</span>
            {[
              { v: 'off', label: 'Off' },
              { v: 'conformance', label: 'Conformance', disabled: conformanceUnavailable, title: conformanceUnavailable ? 'Conformance unavailable for this source' : 'Tint nodes by deviation rate' },
              { v: 'errors', label: 'Errors', title: 'Tint nodes by error rate' },
              { v: 'volume', label: 'Volume', title: 'Tint nodes by event count' },
              { v: 'latency', label: 'Latency', title: 'Tint nodes by mean active time' },
            ].map((opt) => {
              const active = heatmapMode === opt.v;
              return (
                <button key={opt.v}
                  onClick={() => !opt.disabled && setHeatmapMode(opt.v)}
                  disabled={opt.disabled}
                  title={opt.title}
                  style={{
                    background: active ? COLORS.accent : 'transparent',
                    color: active ? '#fff' : (opt.disabled ? COLORS.faint : COLORS.text),
                    border: `1px solid ${active ? COLORS.accent : COLORS.border}`,
                    borderRadius: 4, padding: '3px 10px', fontSize: 11, fontWeight: 600,
                    cursor: opt.disabled ? 'not-allowed' : 'pointer',
                    opacity: opt.disabled ? 0.5 : 1,
                  }}>
                  {opt.label}
                </button>
              );
            })}
            {heatmapData && (
              <div style={{
                marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6,
                color: COLORS.faint,
              }}>
                <span>{heatmapData.label}</span>
                <span style={{ fontSize: 10 }}>{heatmapData.legend?.split(' → ')[0]}</span>
                <div style={{
                  width: 80, height: 8, borderRadius: 4,
                  background: 'linear-gradient(to right, rgb(34,197,94), rgb(245,158,11), rgb(239,68,68))',
                  border: `1px solid ${COLORS.border}`,
                }} />
                <span style={{ fontSize: 10 }}>{heatmapData.legend?.split(' → ')[1]}</span>
              </div>
            )}
            {heatmapMode === 'conformance' && conformanceUnavailable && (
              <span style={{ marginLeft: 'auto', color: COLORS.faint, fontSize: 10 }}>
                Conformance unavailable — pick another dimension.
              </span>
            )}
          </div>
          {/* Top: DFG */}
          <div style={{
            flex: 1, minHeight: 280, overflow: 'auto',
            borderBottom: `1px solid ${COLORS.border}`,
          }}>
            <DfgCanvas
              graph={graph}
              onSelectNode={setSelectedNodeId}
              selectedNodeId={selectedNodeId}
              heatmap={heatmapData}
            />
          </div>
          {/* Bottom: Variants + Bottlenecks + Conformance rails */}
          <div style={{ display: 'flex', height: '50%', minHeight: 260 }}>
            <div style={{
              flex: 1, overflow: 'auto',
              borderRight: `1px solid ${COLORS.border}`,
            }}>
              <div style={{
                padding: '8px 12px', fontSize: 12, fontWeight: 700,
                color: COLORS.muted, borderBottom: `1px solid ${COLORS.border}`,
                background: COLORS.panel, position: 'sticky', top: 0,
              }}>
                Variants
              </div>
              <VariantsList
                variants={variants}
                locked={variantsLocked}
                lockedMessage={variantsLockedMsg}
              />
            </div>
            <div style={{
              flex: 1, overflow: 'auto',
              borderRight: `1px solid ${COLORS.border}`,
            }}>
              <div style={{
                padding: '8px 12px', fontSize: 12, fontWeight: 700,
                color: COLORS.muted, borderBottom: `1px solid ${COLORS.border}`,
                background: COLORS.panel, position: 'sticky', top: 0,
              }}>
                Bottlenecks
              </div>
              <BottlenecksList
                bottlenecks={bottlenecks}
                selectedNodeId={selectedNodeId}
                onSelect={setSelectedNodeId}
              />
            </div>
            <div style={{ flex: 1.2, overflow: 'auto' }}>
              <div style={{
                padding: '8px 12px', fontSize: 12, fontWeight: 700,
                color: COLORS.muted, borderBottom: `1px solid ${COLORS.border}`,
                background: COLORS.panel, position: 'sticky', top: 0,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                Conformance
                {conformance && conformance.fitness_score != null && (
                  <span style={{
                    fontSize: 10,
                    color: conformance.fitness_score >= 0.9 ? COLORS.good
                         : conformance.fitness_score >= 0.7 ? COLORS.warn
                         : COLORS.bad,
                    fontWeight: 800,
                  }}>
                    {Math.round(conformance.fitness_score * 100)}% fitness
                  </span>
                )}
              </div>
              <ConformancePanel
                conformance={conformance}
                locked={conformanceLocked}
                lockedMessage={conformanceLockedMsg}
                selectedNodeId={selectedNodeId}
                onSelect={setSelectedNodeId}
              />
            </div>
          </div>
        </div>
      )}

      {simOpen && analysis && (
        <SimulationPanel
          sourceType={sourceType}
          sourceId={sourceId}
          analysis={analysis}
          onClose={() => setSimOpen(false)}
        />
      )}
    </div>
  );
}
